import React, { useRef } from 'react';
import { Box, Flex } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { SearchSharedListsDropdown } from 'components/molecules/searchSharedListsDropdown';
import { useAddListToSingleContacts } from 'components/organisms/contactActionPanel/hooks/useAddListToSingleContacts';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'styled-components';

export const ContactListRenderer = ({ data, value }) => {
  const { colors } = useTheme();
  const { onChangeList, onCreateList, isLoading } = useAddListToSingleContacts({
    queryParams: data.options.queryParams,
    contact: data.original,
    activeNetwork: data.options.activeNetwork,
    origin: 'card_lists_management',
    page: 'contacts',
  });

  const ref = useRef(null);

  if (data.options.showLoader) {
    return (
      <ContentLoader
        speed={2}
        width={400}
        height={120}
        viewBox="0 0 400 120"
        backgroundColor={colors.neutral[50]}
        foregroundColor={colors.neutral[20]}
      >
        <rect x="20" y="16" rx="3" ry="3" width="80" height="8" />
      </ContentLoader>
    );
  }

  if (!value || value?.length === 0) {
    return '';
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        '&:hover': {
          '#contact-lists-dropdown': {
            opacity: '1',
          },
        },
      }}
    >
      <Flex
        p="8px"
        alignItems="center"
        sx={{
          width: 'max-content',
          height: '100%',
          justifyContent: 'space-between',
        }}
      >
        {value?.length > 0 && (
          <Flex ref={ref} sx={{ gap: '4px', alignItems: 'center', width: 'max-content', color: 'text.main' }}>
            {value?.map((list) => (
              <Box
                key={list.id}
                width="max-content"
                maxHeight="24px"
                sx={{
                  color: 'text.main',
                  backgroundColor: 'neutral.0',
                  borderRadius: '99px',
                  padding: '4px 12px',
                  border: '1px solid',
                  borderColor: 'border.subtle',
                  fontSize: '14px',
                  lineHeight: '16px',
                }}
              >
                {list.name}
              </Box>
            ))}
          </Flex>
        )}

        <SearchSharedListsDropdown
          placement="bottom-end"
          trigger={
            <Box
              id="contact-lists-dropdown"
              sx={{
                opacity: '0',
                display: 'flex',
                bg: 'neutral.20',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                top: '0px',
                right: '0px',
                bottom: '0px',
                padding: '8px',
              }}
            >
              <Box
                as={ChevronDown}
                sx={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer',
                  color: 'text.main',
                  display: 'block',
                }}
              />
            </Box>
          }
          value={value?.map((item) => ({ id: item.id, label: item.name, value: item.id })) ?? []}
          onChange={onChangeList}
          onCreate={onCreateList}
          isLoading={isLoading}
        />
      </Flex>
    </Box>
  );
};

ContactListRenderer.propTypes = {
  data: PropTypes.object.isRequired,
  value: PropTypes.array,
};

ContactListRenderer.defaultProps = {
  value: [],
};
